import React, { useContext, useState, FormEvent } from 'react';

import { Button, PopUp, Dialog, DialogHeader, DialogContent, DialogButtons, Input } from '@ergeon/core-components';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { PhoneArrowUpRightIcon, UsersIcon, ArrowLeftIcon } from '@ergeon/icons/hero/micro';

import { type ModalHandler } from '../index';
import { TwilioContext } from '../../context-providers/TwilioContext';
import { AgentContext } from '../../context-providers/AgentContext';
import { AgentSelectionView } from '../../components/AgentSelection';
import { Agent } from '../../types';
import { useModalClose } from '../../hooks/useModalClose';

type Props = {
  modal: ModalHandler;
  to?: string;
};

type View = 'dialer' | 'agents';

const DialerModal: React.FC<Props> = ({ modal, to }) => {
  const [view, setView] = useState<View>('dialer');
  const [phoneNumber, setPhoneNumber] = useState<string>(to || '');
  const [isDisabled, setIsDisabled] = useState(false);
  const [error, setError] = useState<string>();

  const { makeCall } = useContext(TwilioContext);
  const { identity } = useContext(AgentContext);
  const closeModal = useModalClose(modal);

  const onMakeCall = async() => {
    if (!identity) {
      console.error('No identity available for making a call');
      setError('No identity available for making a call');
      return;
    }

    if (!phoneNumber) {
      console.error('We need a phone number to make a call');
      setError('We need a phone number to make a call');
      return;
    }

    if (phoneNumber.startsWith('+1999')) {
      console.error('Calling internal fake phone number');
      setError('You are trying to call internal fake phone number');
      return;
    }

    if (!isValidPhoneNumber(phoneNumber, 'US')) {
      console.error('Phone number is not valid');
      setError('Phone number is not valid');
      return;
    }

    setIsDisabled(true);

    await makeCall(phoneNumber);

    setIsDisabled(false);
    closeModal();
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    onMakeCall();
  };

  const onPhoneNumberChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    const formattedForValidation = digitsOnly.length === 10 ? `+1${digitsOnly}` : value;

    setPhoneNumber(formattedForValidation);
    setError(undefined);
  };

  const getDisplayValue = (phone: string) => {
    if (phone.startsWith('+1') && phone.length === 12) {
      const digits = phone.slice(2);
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const onSelectStaffAgent = () => {
    setView('agents');
  };

  const onAgentSelected = async(agent: Agent) => {
    await makeCall(agent.identity);
    closeModal();
  };

  const onBack = () => {
    setView('dialer');
  };

  const title = view === 'dialer' ? 'Dialer' : 'Select Agent';
  const backButton =
    view === 'agents' ? (
      <Button
        ariaLabel="Back to dialer"
        className="shrink-0"
        flavor="regular"
        isSquare
        onClick={onBack}
        size="medium"
        taste="boundless">
        <ArrowLeftIcon className="size-5 shrink-0" />
      </Button>
    ) : undefined;

  return (
    <PopUp isVisible={modal.visible} maxWidth={view === 'dialer' ? 'xsmall' : 'large'} onChangeVisible={closeModal}>
      <Dialog isVisible={modal.visible}>
        <DialogHeader icon={backButton} onDismiss={closeModal} title={title} />
        <DialogContent>
          {view === 'dialer' ? (
            <form
              className="flex flex-col gap-4"
              data-testid="dialer-modal-form"
              id="dialer-modal-form"
              onSubmit={handleFormSubmit}>
              <Input
                aria-label="Phone number input"
                {...(error && { isValid: false })}
                label="Enter a phone number"
                onChange={(_e, _name, value) => onPhoneNumberChange(value || '')}
                placeholder="Enter phone number"
                size="small"
                type="phone"
                validationMessage={error}
                value={getDisplayValue(phoneNumber)} />

              <DialogButtons>
                <Button
                  aria-label="Make call to entered number"
                  className="whitespace-nowrap"
                  disabled={isDisabled}
                  flavor="success"
                  isFullWidth
                  onClick={() => onMakeCall()}
                  taste="solid"
                  title="Call this number"
                  type="submit">
                  <PhoneArrowUpRightIcon className="size-5 shrink-0" />
                  {isDisabled ? 'Loading...' : 'Call'}
                </Button>
                <Button
                  aria-label="Open staff agent selection"
                  className="whitespace-nowrap"
                  flavor="regular"
                  isFullWidth
                  onClick={onSelectStaffAgent}
                  taste="line"
                  title="Select a staff agent to call"
                  type="button">
                  <UsersIcon className="size-5 shrink-0" />
                  Select Staff Agent
                </Button>
              </DialogButtons>
            </form>
          ) : (
            <AgentSelectionView
              excludeIdentity={identity}
              onSelect={onAgentSelected}
              title="Select an agent from the list" />
          )}
        </DialogContent>
      </Dialog>
    </PopUp>
  );
};

export default DialerModal;
